/**
 * guideContent.ts — Contextual integration guide content for each admin page.
 *
 * Each page has a GuideContent object describing how to integrate with that
 * particular billing feature from an external application.
 *
 * @module packages/billing-admin/src/guides/guideContent
 * @since 2026-06-08
 */

import type { GuideContent } from '../components/IntegrationGuide';

// ---------------------------------------------------------------------------
// Usage Pricing
// ---------------------------------------------------------------------------

export const usagePricingGuide: GuideContent = {
  title: 'Usage-Based Billing',
  description:
    'Usage-based billing lets you charge customers based on actual consumption. Define event types (the things you measure), create pricing catalogs (bundles of event types with credit costs), then report usage events from your application via the API.',
  sections: [
    {
      title: '1. Concepts',
      content: `• Event Type — A billable action (e.g. "api_call", "file_indexed", "ai_inference"). Define these in the Master Event Types table.
• Pricing Catalog — A named bundle that maps event types to credit costs. Assign a catalog to a plan.
• Usage Event — A single occurrence reported by your app. Each event deducts credits based on the catalog price.
• Credits — Pre-paid balance. Customers buy credits, usage deducts them.`,
    },
    {
      title: '2. Setup Steps',
      content: `1. Create master event types (this page → "Event Types" tab)
2. Create a pricing catalog (this page → "Catalogs" tab)
3. Add event types to the catalog with credit costs
4. Assign the catalog to a plan (Plan Catalog page)
5. Report usage events via API from your application`,
    },
    {
      title: '3. Report Usage Events',
      content: 'POST a usage event from your application whenever a billable action occurs. Include the customer ID, event type, and quantity.',
      code: `// POST /usage/events
// Headers: Authorization: Bearer <service_token>

fetch('https://finance.rule42.ai/usage/events', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <SERVICE_API_KEY>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    customer_id: '<CUSTOMER_UUID>',
    event_type: 'ai_inference',
    quantity: 1,
    metadata: { model: 'claude-4', tokens: 1500 }
  })
})`,
    },
    {
      title: '4. Batch Usage Reporting',
      content: 'For high-volume applications, batch multiple events in a single request.',
      code: `// POST /usage/events/batch
fetch('https://finance.rule42.ai/usage/events/batch', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <SERVICE_API_KEY>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    events: [
      { customer_id: '<UUID>', event_type: 'api_call', quantity: 10 },
      { customer_id: '<UUID>', event_type: 'file_indexed', quantity: 3 },
    ]
  })
})`,
    },
    {
      title: '5. Query Usage & Balance',
      content: 'Check a customer\'s remaining credits and usage summary.',
      code: `// GET /credits/balance?customerId=<UUID>
// Response: { balance_cents: 450000, reserved_cents: 0, available_cents: 450000 }

// GET /usage/summary?customerId=<UUID>&periodStart=2026-06-01&periodEnd=2026-06-30
// Response: [{ event_type, quantity, credits_consumed, date }]`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------

export const customersGuide: GuideContent = {
  title: 'Customer Management',
  description:
    'Customers represent billable entities (organizations or individuals). They are typically synced from your identity/auth service via webhooks, but can also be created directly via API.',
  sections: [
    {
      title: '1. Auto-Sync via Webhooks',
      content: `The recommended approach: configure your auth/identity service to fire webhooks when organizations are created. rule42-billing will automatically create the customer record, subscription, and initial credit balance.

Required webhook events:
• subscription.created — new org registered
• subscription.updated — plan changed
• member.added / member.removed — seat count updates`,
    },
    {
      title: '2. Create Customer via API',
      content: 'Alternatively, create customers directly from your application.',
      code: `// POST /admin/customers
fetch('https://finance.rule42.ai/admin/customers', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <ADMIN_TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    name: 'Acme Corp',
    email: 'billing@acme.com',
    metadata: { plan_code: 'pro', seat_count: 5 }
  })
})`,
    },
    {
      title: '3. Customer Lifecycle Events',
      content: `Your application should notify billing when:
• Customer upgrades/downgrades plan
• Customer cancels subscription
• Customer's seat count changes
• Customer's billing email changes

Use the webhook system or call the API directly.`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Webhooks
// ---------------------------------------------------------------------------

export const webhooksGuide: GuideContent = {
  title: 'Webhooks & Event System',
  description:
    'Webhooks allow rule42-billing to notify your applications when billing events occur (outbound), and allow external systems to notify billing of changes (inbound). Configure endpoints, subscribe to event types, and manage delivery.',
  sections: [
    {
      title: '1. Inbound Webhooks (External → Billing)',
      content: `Inbound webhooks let external services (auth, CRM, etc.) notify billing of changes.

The auth webhook endpoint accepts HMAC-signed payloads:
• URL: POST /webhooks/auth
• Signature: X-Auth-Signature header (HMAC-SHA256)
• Events: subscription.created, subscription.updated, member.added, member.removed`,
      code: `// Sending an inbound webhook to billing
const crypto = require('crypto');
const body = JSON.stringify({
  eventId: 'evt_' + crypto.randomUUID(),
  event: 'subscription.created',
  tenantId: '<ORG_UUID>',
  data: { org_name: 'Acme', plan_code: 'pro', seat_count: 5 }
});
const sig = crypto.createHmac('sha256', SHARED_SECRET)
  .update(body).digest('hex');

fetch('https://finance.rule42.ai/webhooks/auth', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-Auth-Signature': sig
  },
  body
});`,
    },
    {
      title: '2. Outbound Webhooks (Billing → Your App)',
      content: `Outbound webhooks notify YOUR applications when billing events happen.

Available event categories:
• customer.* — customer created, updated, deleted
• subscription.* — subscription lifecycle
• credits.* — balance changes, low balance alerts
• usage.* — usage thresholds, budget exceeded
• invoice.* — invoice generated, paid, overdue
• plan.* — plan changes, permission updates

Each endpoint receives HMAC-signed POST requests with a JSON payload.`,
    },
    {
      title: '3. Webhook Payload Format',
      content: 'All outbound webhook payloads follow this structure:',
      code: `{
  "id": "evt_abc123",
  "type": "credits.low_balance",
  "created_at": "2026-06-08T10:30:00Z",
  "data": {
    "customer_id": "<UUID>",
    "customer_name": "Acme Corp",
    "balance_cents": 500,
    "threshold_cents": 1000
  }
}`,
    },
    {
      title: '4. Verifying Webhook Signatures',
      content: 'Always verify the HMAC signature before processing.',
      code: `// Node.js verification example
const crypto = require('crypto');

function verifyWebhook(rawBody, signature, secret) {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(rawBody)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expected)
  );
}`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------

export const creditsGuide: GuideContent = {
  title: 'Credits & Balances',
  description:
    'Credits are the billing currency. Customers pre-pay for credits, which are deducted as they use your platform. Monitor balances, grant manual top-ups, and configure low-balance alerts.',
  sections: [
    {
      title: '1. How Credits Work',
      content: `• Each customer has a credit balance (in cents, e.g. 500000 = $5,000)
• Usage events deduct credits based on the pricing catalog
• Credits cannot go below zero (enforced at DB level)
• When balance is low, outbound webhooks fire "credits.low_balance"
• Executions may be blocked when balance hits zero`,
    },
    {
      title: '2. Grant Credits via API',
      content: 'Top up a customer\'s balance programmatically.',
      code: `// POST /admin/credits/grant
fetch('https://finance.rule42.ai/admin/credits/grant', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer <ADMIN_TOKEN>',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    customerId: '<CUSTOMER_UUID>',
    amountCents: 100000,  // $1,000
    reason: 'Monthly credit allocation'
  })
})`,
    },
    {
      title: '3. Check Balance from Your App',
      content: 'Query a customer\'s balance before allowing expensive operations.',
      code: `// GET /credits/balance?customerId=<UUID>
// Response:
{
  "balance_cents": 450000,
  "reserved_cents": 5000,
  "available_cents": 445000
}

// Use available_cents to decide if an operation can proceed`,
    },
    {
      title: '4. Pre-Authorization',
      content: `For long-running operations, reserve credits upfront:
1. Call /credits/pre-authorize to reserve N credits
2. Run the operation
3. Call /credits/settle to finalize the actual cost
4. If operation fails, call /credits/release to return reserved credits`,
      code: `// POST /credits/pre-authorize
// { customerId, amountCents: 5000, reason: "atp_execution" }
// Response: { reservation_id: "res_abc123" }

// POST /credits/settle
// { reservation_id: "res_abc123", actualAmountCents: 3200 }`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Plans
// ---------------------------------------------------------------------------

export const plansGuide: GuideContent = {
  title: 'Plan Configuration',
  description:
    'Plans define what features and pricing a customer receives. Each plan can have flat pricing, per-seat pricing, tiered pricing, or usage-based pricing (linked to a pricing catalog).',
  sections: [
    {
      title: '1. Plan Types',
      content: `• Flat — Fixed monthly/annual fee
• Per-Seat — Price × number of seats
• Tiered — Different prices at different volume tiers
• Usage — Credits deducted per usage event (requires a pricing catalog)

Plans can combine types (e.g. flat base + usage overage).`,
    },
    {
      title: '2. Assigning Plans to Customers',
      content: 'Plans are assigned via subscriptions. When a customer signs up, create a subscription linking them to a plan.',
      code: `// Typically done via webhook:
// subscription.created event with plan_code in the data

// Or directly via API:
// POST /admin/subscriptions
{
  "customer_id": "<UUID>",
  "plan_id": "<PLAN_UUID>",
  "status": "active"
}`,
    },
    {
      title: '3. Plan Permissions',
      content: `Plans can grant permissions/entitlements. When a customer subscribes to a plan, your app should check their entitlements.

Configure plan → permissions mappings here, then query entitlements at runtime.`,
      code: `// GET /entitlements?customerId=<UUID>
// Response:
{
  "plan": "pro",
  "features": ["advanced_ai", "custom_protocols", "team_sharing"],
  "limits": { "seats": 10, "projects": 50 }
}`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export const subscriptionsGuide: GuideContent = {
  title: 'Subscriptions',
  description:
    'A subscription links a customer to a plan with a specific billing period. Manage lifecycle events (creation, upgrade, downgrade, cancellation) from here or via API/webhooks.',
  sections: [
    {
      title: '1. Subscription Lifecycle',
      content: `States: active → paused → cancelled → expired
• active: Customer is billed, has full access
• paused: Billing suspended, limited access
• cancelled: Customer initiated cancel, access until period end
• expired: Access revoked`,
    },
    {
      title: '2. Trigger from External App',
      content: 'Fire a webhook when a customer changes their plan in your app.',
      code: `// Webhook payload for subscription change:
{
  "eventId": "evt_...",
  "event": "subscription.updated",
  "tenantId": "<ORG_UUID>",
  "data": {
    "plan_code": "enterprise",
    "seat_count": 15,
    "change_type": "upgrade"
  }
}`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Invoices
// ---------------------------------------------------------------------------

export const invoicesGuide: GuideContent = {
  title: 'Invoices',
  description:
    'Invoices are generated automatically at the end of each billing period, or manually for one-off charges. They aggregate subscription fees and usage charges.',
  sections: [
    {
      title: '1. Auto-Generation',
      content: `Invoices are generated automatically when:
• A billing period ends (monthly/annual)
• A customer's credit balance is recharged
• An enterprise contract milestone is reached`,
    },
    {
      title: '2. Webhook Notifications',
      content: 'Subscribe to invoice events to trigger payment collection or notifications.',
      code: `// Outbound webhook events:
// invoice.created — new invoice generated
// invoice.paid — payment received
// invoice.overdue — past due date
// invoice.voided — invoice cancelled`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Revenue
// ---------------------------------------------------------------------------

export const revenueGuide: GuideContent = {
  title: 'Revenue Analytics',
  description: 'Track MRR, ARR, churn, and cohort metrics. These are calculated automatically from subscription and payment data.',
  sections: [
    {
      title: '1. Available Metrics',
      content: `• MRR (Monthly Recurring Revenue) — sum of all active monthly subscription fees
• ARR (Annual Recurring Revenue) — MRR × 12
• Churn Rate — percentage of customers who cancelled in a period
• Cohort Retention — how long customers stay by signup month`,
    },
    {
      title: '2. API Access',
      content: 'Query metrics programmatically for dashboards or reporting.',
      code: `// GET /admin/reports/mrr
// GET /admin/reports/arr
// GET /admin/reports/churn?period=30d
// GET /admin/reports/cohorts`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Audit Log
// ---------------------------------------------------------------------------

export const auditLogGuide: GuideContent = {
  title: 'Audit Log',
  description: 'Every billing action is recorded in the audit log. Use this for compliance, debugging, and accountability.',
  sections: [
    {
      title: '1. What Gets Logged',
      content: `• Customer create/update/delete
• Subscription changes
• Credit grants and deductions
• Webhook deliveries (inbound and outbound)
• Admin actions (plan changes, manual overrides)
• API key usage`,
    },
    {
      title: '2. Query via API',
      code: `// GET /admin/audit-logs?event_type=credits.grant&limit=50
// GET /admin/audit-logs?customer_id=<UUID>&since=2026-06-01`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

export const overviewGuide: GuideContent = {
  title: 'Getting Started',
  description: 'rule42-billing is a usage-based billing platform. This overview shows your billing health at a glance. Here\'s how to get started integrating.',
  sections: [
    {
      title: '1. Quick Start',
      content: `1. Set up auth webhook (Webhooks page) — syncs customers automatically
2. Define event types (Usage Pricing page) — what you bill for
3. Create a pricing catalog — how much each event costs
4. Assign catalog to a plan (Plans page)
5. Report usage events from your app via API
6. Monitor on this dashboard`,
    },
    {
      title: '2. API Authentication',
      content: 'All API calls require a Bearer token. Admin operations require the admin role.',
      code: `// All requests:
headers: {
  'Authorization': 'Bearer <TOKEN>',
  'Content-Type': 'application/json'
}

// Tokens are issued by your OIDC provider.
// Admin endpoints require: role=admin OR org_role=owner`,
    },
    {
      title: '3. Base URL',
      content: 'All API endpoints are relative to your billing server URL.',
      code: `// Production
https://finance.rule42.ai

// Endpoints:
// /admin/*        — Admin console API (requires admin role)
// /webhooks/*     — Inbound webhook receivers
// /usage/*        — Usage event reporting
// /credits/*      — Credit balance queries
// /entitlements/* — Permission/feature checks`,
    },
  ],
};

// ---------------------------------------------------------------------------
// User Budgets
// ---------------------------------------------------------------------------

export const userBudgetsGuide: GuideContent = {
  title: 'User Budgets',
  description: 'User budgets let org admins set per-user spending limits. When a user exceeds their budget, operations are blocked until the next period or an admin increases the limit.',
  sections: [
    {
      title: '1. How Budgets Work',
      content: `• Org-level credit balance is the hard limit
• User budgets are soft limits within that balance
• When a user exceeds their budget, their requests fail
• Budgets reset at the start of each billing period
• Admins can adjust budgets at any time`,
    },
    {
      title: '2. Check Budget from Your App',
      code: `// GET /budgets/check?customerId=<ORG>&userId=<USER>
// Response:
{
  "budget_cents": 50000,
  "spent_cents": 32000,
  "remaining_cents": 18000,
  "can_proceed": true
}`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export const contractsGuide: GuideContent = {
  title: 'Enterprise Contracts',
  description: 'Enterprise contracts define custom pricing, committed spend, and volume discounts for large customers.',
  sections: [
    {
      title: '1. Contract Structure',
      content: `• Committed spend — minimum annual spend guaranteed
• Custom pricing — override standard catalog prices
• Volume discounts — tiered pricing at high volumes
• Payment terms — net-30, net-60, custom schedules`,
    },
    {
      title: '2. Create via API',
      code: `// POST /admin/enterprise/contracts
{
  "customer_id": "<UUID>",
  "committed_spend_cents": 12000000,
  "discount_percent": 15,
  "payment_terms": "net-30",
  "start_date": "2026-07-01",
  "end_date": "2027-06-30"
}`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export const reconciliationGuide: GuideContent = {
  title: 'Reconciliation',
  description: 'Match bank transactions to invoices and credit purchases. Ensures your billing records align with actual payments received.',
  sections: [
    {
      title: '1. Process',
      content: `1. Import bank transactions (CSV or API)
2. Auto-match by amount + reference
3. Review unmatched transactions
4. Manually match or flag for investigation`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Operators
// ---------------------------------------------------------------------------

export const operatorsGuide: GuideContent = {
  title: 'Operators',
  description: 'Operators are admin users or service accounts that interact with the billing API. Manage access and audit their actions here.',
  sections: [
    {
      title: '1. Access Control',
      content: `Admin access is controlled via your OIDC provider. Users need one of:
• role = "admin" in their JWT
• org_role = "owner" or "admin"
• permissions array includes "platform.admin" or "billing.admin"`,
    },
  ],
};

// ---------------------------------------------------------------------------
// Pricing Pages
// ---------------------------------------------------------------------------

export const pricingPagesGuide: GuideContent = {
  title: 'Dynamic Pricing Pages',
  description:
    'Pricing Pages let you create, customize, and publish dynamic pricing comparison pages without writing any frontend code. They pull live data from your plans, prices, and marketing features — so when you update a plan or change a price, your public pricing page updates automatically.',
  sections: [
    {
      title: '1. What Pricing Pages Do',
      content: `Dynamic Pricing Pages solve a common problem: keeping your public-facing pricing page in sync with your actual billing configuration. Instead of hardcoding prices in your marketing site, you configure everything here and serve it via API.

Each pricing page has:
• A unique slug (e.g. "default", "enterprise", "partner-program")
• Assigned plans with display order, badges ("Most Popular"), and CTA overrides
• Marketing features pulled from your feature catalog
• Theming (colors, custom CSS, logo, header/footer HTML)
• Public endpoints — JSON for custom rendering, HTML for instant deployment`,
    },
    {
      title: '2. Setup Steps',
      content: `1. Create Marketing Features — Go to Marketing Features and add the feature bullets you want on your pricing cards (e.g. "Unlimited Projects", "Priority Support", "API Access")
2. Assign Features to Plans — For each plan, map which marketing features are included/excluded and set display text
3. Create a Pricing Page — Give it a slug, title, subtitle, and signup URL
4. Add Plans to the Page — Select which plans appear, set their order, add badges, configure per-plan CTA buttons
5. Configure Theme — Set brand colors, add your logo URL, custom header/footer HTML
6. Publish — Toggle the page to "Published" to make it publicly accessible`,
    },
    {
      title: '3. Integration Options',
      content: `There are three ways to use your pricing page:

Option A: Standalone HTML Page (Fastest)
Point a subdomain or route at the HTML endpoint. The page is fully rendered with responsive CSS, your branding, and a "Powered by RULE42" footer.
URL: GET /pricing/{orgId}/{slug}/html
Example: https://finance.rule42.ai/pricing/your-org-id/default/html

Option B: Embed via iframe
Embed the pricing page inside your existing site using an iframe. Use ?embed=true for a compact version without the outer footer links.
<iframe src="https://finance.rule42.ai/pricing/{orgId}/{slug}/html?embed=true" width="100%" height="800" frameborder="0"></iframe>

Option C: JSON API (Full Customization)
Fetch the structured data and render with your own components. Best for custom designs that need to match your exact brand.
URL: GET /pricing/{orgId}/{slug}
Returns: { page, plans: [{ plan, prices, features, config }] }`,
      code: `// Fetch pricing data and render with your own React/Vue/Svelte components
const response = await fetch(
  'https://finance.rule42.ai/pricing/YOUR_ORG_ID/default'
);
const { page, plans } = await response.json();

// Each plan contains:
// - plan: { id, name, description, interval, trialDays }
// - prices: [{ unitAmount, currency }]
// - features: [{ included, valueText, feature: { name, icon } }]
// - config: { badge, isHighlighted, ctaTextOverride }`,
    },
    {
      title: '4. Theming & Customization',
      content: `Customize the look and feel via the Theme Config on your pricing page:

Colors:
• primaryColor — CTA buttons, highlights, checkmarks (default: #0F766E)
• backgroundColor — Page background (default: #F8FAFC)
• cardBackground — Plan card background (default: #FFFFFF)
• textColor — Body text color (default: #1E293B)
• badgeColor — Badge ribbon color (default: #F59E0B)

Header & Footer:
• logoUrl — URL to your logo image (displayed above the title)
• headerHtml — Custom HTML injected in the page header area
• footerHtml — Custom HTML injected above the standard footer links

Advanced:
• Custom CSS — Write custom CSS rules that override any default styles
• Per-plan CTA overrides — Different button text/URLs per plan
• Badge text — "Most Popular", "Best Value", "New", etc.`,
    },
    {
      title: '5. Signup Flow Integration',
      content: `When a visitor clicks a CTA button, they are redirected to your Signup URL with the plan ID appended as a query parameter:

https://yourapp.com/signup?plan={plan-uuid}

Your signup page should:
1. Read the "plan" query parameter
2. Create the customer in billing (POST /customers)
3. Create a subscription for that plan (POST /subscriptions)
4. Redirect to your app's onboarding flow

If you need plan-specific landing pages, use per-plan "Signup URL Override" to point each CTA to a different URL.`,
      code: `// In your signup handler:
const planId = new URLSearchParams(window.location.search).get('plan');

// After user registers, create their subscription:
const res = await fetch('/api/billing/subscribe', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    customerId: newUser.billingCustomerId,
    planId: planId,
  }),
});`,
    },
    {
      title: '6. Multiple Pricing Pages',
      content: `You can create multiple pricing pages for different audiences:

• /pricing/default — Your main public pricing page
• /pricing/enterprise — Enterprise-focused with annual plans only
• /pricing/partner — Partner/reseller pricing with different rates
• /pricing/promo-summer — Seasonal promotional pricing

Each page can show different plans, different badges, different CTA URLs. This lets you A/B test pricing presentations, create audience-specific pages, or run time-limited promotions without touching code.

Set one page as "Default" — this is the page served when no slug is specified.`,
    },
    {
      title: '7. Keeping Pages Fresh',
      content: `Pricing pages are rendered on-demand from live data. When you:
• Change a plan name → reflected immediately
• Update a price → reflected immediately
• Add/remove marketing features → reflected immediately
• Change plan assignments → reflected immediately

No rebuild, no deploy, no cache-busting needed. The HTML and JSON endpoints always serve current data.

Tip: Use the "Preview" button in the admin to see exactly what visitors will see before publishing.`,
    },
  ],
};
